// client/src/pages/Plans.tsx

import { useState } from "react";
import { useLocation } from "wouter";
import { PlanFeatures } from "@/components/PlanFeatures";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { ArrowLeft, Crown, CheckCircle, AlertCircle } from "lucide-react";
import { getCurrentOwner } from "@/lib/auth";
import { PlanType, PLAN_NAMES, PLAN_PRICES } from "@shared/plans";
import { toast } from "@/hooks/use-toast";

export default function PlansPage() {
  const [, setLocation] = useLocation();
  const owner = getCurrentOwner();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);

  const handleUpgrade = async (plan: PlanType) => {
    if (!owner) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para cambiar de plan",
        variant: "destructive",
      });
      return;
    }

    setSelectedPlan(plan);
    setLoading(true);

    try {
      // Aquí iría la llamada a la API para cambiar el plan
      // Por ahora simulamos una pausa para mostrar el loading
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: "✅ Plan actualizado",
        description: `Ahora tienes el plan ${PLAN_NAMES[plan]}`,
      });

      // Recargar la página para actualizar los datos del owner
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Error al cambiar de plan:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el plan. Intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  if (!owner) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-bold mb-2">Acceso no autorizado</h2>
            <p className="text-muted-foreground mb-6">
              Debes iniciar sesión como dueño para ver los planes disponibles.
            </p>
            <Button onClick={() => setLocation("/shop/login")}>
              Ir a Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentPlanType = (owner.planType || "basic") as PlanType;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation("/owner/dashboard")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al Dashboard
        </Button>
        <h1 className="text-3xl font-bold">Planes y Precios</h1>
      </div>

      {/* Información del plan actual */}
      <Card className="mb-8 border-purple-200 bg-purple-50/30">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Crown className="h-6 w-6 text-purple-600" />
            <CardTitle className="text-xl">
              Tu plan actual: {PLAN_NAMES[currentPlanType]}
            </CardTitle>
          </div>
          <CardDescription>
            {currentPlanType === "basic" &&
              "Disfruta de las características básicas para comenzar."}
            {currentPlanType === "professional" &&
              "Estás aprovechando al máximo tu negocio en crecimiento."}
            {currentPlanType === "enterprise" &&
              "Tienes acceso a todas las características premium."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Próximo pago</p>
              <p className="font-medium">
                {owner.subscriptionEndDate
                  ? new Date(owner.subscriptionEndDate).toLocaleDateString()
                  : owner.trialEndDate
                    ? `Período de prueba hasta: ${new Date(owner.trialEndDate).toLocaleDateString()}`
                    : "Sin fecha definida"}
              </p>
            </div>
            {currentPlanType !== "enterprise" && (
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => handleUpgrade("enterprise")}
                disabled={loading}
              >
                {loading && selectedPlan === "enterprise"
                  ? "Procesando..."
                  : "Mejorar a Enterprise"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabla comparativa de planes */}
      <PlanFeatures currentPlan={currentPlanType} onUpgrade={handleUpgrade} />

      {/* Preguntas frecuentes */}
      <Card className="mt-12">
        <CardHeader>
          <CardTitle className="text-xl">Preguntas frecuentes</CardTitle>
          <CardDescription>
            Todo lo que necesitas saber sobre nuestros planes y facturación
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold mb-2">
                ¿Puedo cambiar de plan en cualquier momento?
              </h3>
              <p className="text-sm text-muted-foreground">
                Sí, puedes cambiar de plan cuando lo necesites. Los cambios se
                aplicarán inmediatamente.
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-2">¿Cómo funciona la facturación?</h3>
              <p className="text-sm text-muted-foreground">
                La facturación es mensual. Puedes pagar por transferencia
                bancaria o efectivo.
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-2">¿Hay período de prueba?</h3>
              <p className="text-sm text-muted-foreground">
                Todos los planes nuevos incluyen 30 días de prueba gratuita sin
                compromiso.
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-2">¿Qué métodos de pago aceptan?</h3>
              <p className="text-sm text-muted-foreground">
                Aceptamos transferencia bancaria y efectivo. Pronto tendremos
                pasarela de pago online.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mensaje de soporte */}
      <div className="mt-8 text-center">
        <p className="text-sm text-muted-foreground">
          ¿Tienes dudas? Contáctanos a{" "}
          <a
            href="mailto:fm.izquierdo@nauta.cu"
            className="text-primary hover:underline"
          >
            fm.izquierdo@nauta.cu
          </a>
        </p>
      </div>
    </div>
  );
}
